<script setup lang="ts">
import { onClickOutside, useMediaQuery } from "@vueuse/core";
import moment from "moment";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VFieldLabel from "../Form/VFieldLabel.vue";
import VIcon from "../Icon/VIcon.vue";
import { useFieldId } from "../useFieldId";
import { isTimeWithin, minutesToTime, parseTypedTime, timeToMinutes } from "./dateTime";
import VPickerPopover from "./VPickerPopover.vue";
import VTimePanel from "./VTimePanel.vue";
import { fieldClearCva, fieldFrameCva, fieldInputCva } from "./VTimePicker.cva";

interface Props {
  /** `HH:mm`, or `[start, end]` with `range`. An ISO date time is read as its local time. */
  modelValue?: string | string[] | null
  placeholder?: string
  size?: "small" | "medium" | "large"
  labelLeft?: string
  required?: boolean
  disabled?: boolean
  errorMessage?: string | null
  showErrorBelow?: boolean
  hasPerms?: boolean
  /** Id of the time input, for an external `<label for>`. Defaults to the surrounding field's id or a unique id. */
  id?: string
  /** Id of an element that names the time input, for a label the caller renders elsewhere instead of `labelLeft`. */
  ariaLabelledby?: string
  /** Id of an element that describes the time input. */
  ariaDescribedby?: string
  /** A start and an end time in one picker, e.g. opening hours. */
  range?: boolean
  /** Step of the minute arrows and of Nu. Typed minutes stay as typed. */
  minuteStep?: 1 | 5 | 10 | 15 | 30
  /** Quick times after Nu. */
  quickTimes?: string[]
  showNow?: boolean
  /** Earliest time that can be chosen (`HH:mm`). */
  minTime?: string | null
  /** Latest time that can be chosen (`HH:mm`). */
  maxTime?: string | null
  textStartTime?: string
  textEndTime?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: undefined,
  size: "medium",
  labelLeft: "",
  required: false,
  disabled: false,
  errorMessage: null,
  showErrorBelow: true,
  hasPerms: true,
  id: undefined,
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
  range: false,
  minuteStep: 5,
  quickTimes: () => ["08:00", "09:00", "12:00", "13:00", "17:00"],
  showNow: true,
  minTime: null,
  maxTime: null,
  textStartTime: undefined,
  textEndTime: undefined,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | string[]): void
  (e: "blur", event: FocusEvent): void
}>();

const { t } = useI18n();
const id = useFieldId(() => props.id);
const isDisabled = computed(() => props.disabled || !props.hasPerms);
const isPhone = useMediaQuery("(max-width: 639px)");

const wrapperRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const popover = ref<InstanceType<typeof VPickerPopover> | null>(null);
const panel = ref<InstanceType<typeof VTimePanel> | null>(null);
const isOpen = ref(false);

/** `HH:mm` of a value as it comes in: a time stays as it is, an ISO date time gives its local time. */
function toTime(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  if (timeToMinutes(value) !== null) {
    return minutesToTime(timeToMinutes(value) as number);
  }
  const parsed = moment(value, moment.ISO_8601, true);
  return parsed.isValid() ? parsed.format("HH:mm") : null;
}

const single = computed(() => {
  if (props.range) {
    return null;
  }
  return toTime(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue);
});
const rangeValue = computed<[string | null, string | null]>(() => {
  const list = Array.isArray(props.modelValue) ? props.modelValue : [];
  return [toTime(list[0]), toTime(list[1])];
});

// --- Single: the field itself takes a typed time ---

const text = ref(single.value ?? "");
const typedError = ref<string | null>(null);

watch(single, (value) => {
  text.value = value ?? "";
  typedError.value = null;
});

function boundsError(time: string): string | null {
  if (isTimeWithin(time, props.minTime, props.maxTime)) {
    return null;
  }
  const min = timeToMinutes(props.minTime);
  if (min !== null && (timeToMinutes(time) as number) < min) {
    return t("date_picker.time_too_early", { time: props.minTime });
  }
  return t("date_picker.time_too_late", { time: props.maxTime });
}

/** Reads what was typed: commits a valid time, clears on empty, shows an error otherwise. Returns whether it took. */
function commitTyped(): boolean {
  const parsed = parseTypedTime(text.value);
  if (parsed === false) {
    typedError.value = t("date_picker.time_invalid");
    return false;
  }
  if (parsed === null) {
    typedError.value = null;
    if (single.value !== null) {
      emit("update:modelValue", "");
    }
    return true;
  }
  const outOfBounds = boundsError(parsed);
  if (outOfBounds) {
    typedError.value = outOfBounds;
    return false;
  }
  typedError.value = null;
  text.value = parsed;
  if (parsed !== single.value) {
    emit("update:modelValue", parsed);
  }
  return true;
}

function commitSingle(time: string): void {
  typedError.value = null;
  text.value = time;
  if (time !== single.value) {
    emit("update:modelValue", time);
  }
  close();
}

function clearSingle(): void {
  typedError.value = null;
  text.value = "";
  if (single.value !== null) {
    emit("update:modelValue", "");
  }
  close();
}

const textChanged = computed(() => text.value !== (single.value ?? ""));

function onFieldKeydown(event: KeyboardEvent): void {
  if (isDisabled.value) {
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    if (textChanged.value || !isOpen.value) {
      if (textChanged.value && commitTyped()) {
        close();
      } else if (!textChanged.value) {
        open();
      }
      return;
    }
    panel.value?.confirm();
    return;
  }
  if (event.key === "Escape" && isOpen.value) {
    event.preventDefault();
    event.stopPropagation();
    text.value = single.value ?? "";
    typedError.value = null;
    close();
    return;
  }
  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
    event.preventDefault();
    stepTyped(event.key === "ArrowUp" ? 1 : -1);
  }
}

/** The arrows in the field move the typed time to the next or previous minute step. */
function stepTyped(direction: 1 | -1): void {
  const parsed = parseTypedTime(text.value);
  const base = parsed ? (timeToMinutes(parsed) as number) : null;
  if (base === null) {
    open();
    return;
  }
  const remainder = base % props.minuteStep;
  const delta = direction === 1 ? props.minuteStep - remainder : -(remainder || props.minuteStep);
  text.value = minutesToTime(base + delta);
  typedError.value = null;
}

function onFieldInput(event: Event): void {
  text.value = (event.target as HTMLInputElement).value;
  typedError.value = null;
}

function onFieldBlur(event: FocusEvent): void {
  emit("blur", event);
  const next = event.relatedTarget as Node | null;
  if (next && wrapperRef.value?.contains(next)) {
    return;
  }
  if (!props.range && textChanged.value) {
    commitTyped();
  }
}

// --- Range: start and end in one picker ---

const activeSide = ref<"start" | "end">("start");
const rangeDraft = ref<[string | null, string | null]>([null, null]);
const endAdjusted = ref(false);

const startLabel = computed(() => props.textStartTime ?? t("date_picker.start_time"));
const endLabel = computed(() => props.textEndTime ?? t("date_picker.end_time"));

function setRangeSide(side: "start" | "end", time: string): void {
  const next: [string | null, string | null] = [...rangeDraft.value];
  next[side === "start" ? 0 : 1] = time;
  endAdjusted.value = false;
  const [start, end] = next;
  if (start && end && (timeToMinutes(end) as number) < (timeToMinutes(start) as number)) {
    next[1] = start;
    endAdjusted.value = true;
  }
  rangeDraft.value = next;
}

function commitRangeSide(time: string): void {
  const side = activeSide.value;
  setRangeSide(side, time);
  const [start, end] = rangeDraft.value;
  // After the start comes the end, unless the range already had one that did not have to move.
  if (side === "start" && (!rangeValue.value[1] || endAdjusted.value)) {
    activeSide.value = "end";
    return;
  }
  if (!start || !end) {
    activeSide.value = start ? "end" : "start";
    return;
  }
  emit("update:modelValue", [start, end]);
  close();
}

function clearRange(): void {
  rangeDraft.value = [null, null];
  if (rangeValue.value[0] || rangeValue.value[1]) {
    emit("update:modelValue", []);
  }
  close();
}

// --- Popover ---

function open(): void {
  if (isDisabled.value || isOpen.value) {
    return;
  }
  popover.value?.show();
}

function close(): void {
  popover.value?.hide();
}

function onShow(): void {
  isOpen.value = true;
  if (props.range) {
    rangeDraft.value = [...rangeValue.value];
    activeSide.value = "start";
    endAdjusted.value = false;
  }
}

function onHide(): void {
  isOpen.value = false;
  endAdjusted.value = false;
  if (isPhone.value) {
    void nextTick(() => inputRef.value?.focus({ preventScroll: true }));
  }
}

function onFieldClick(): void {
  if (isDisabled.value) {
    return;
  }
  open();
}

onClickOutside(wrapperRef, () => {
  if (isOpen.value) {
    close();
  }
}, { ignore: [".v-popper__popper", ".v-picker-sheet"] });

const panelValue = computed(() => {
  if (!props.range) {
    return parseTypedTime(text.value) || single.value;
  }
  return rangeDraft.value[activeSide.value === "start" ? 0 : 1] ?? (activeSide.value === "end" ? rangeDraft.value[0] : null);
});

const panelMinTime = computed(() => {
  if (props.range && activeSide.value === "end" && rangeDraft.value[0]) {
    return rangeDraft.value[0];
  }
  return props.minTime;
});

const clearLabel = computed(() => {
  if (props.range) {
    return t("date_picker.clear");
  }
  return single.value ? t("date_picker.no_time") : t("base.cancel");
});

function onPanelClear(): void {
  if (props.range) {
    clearRange();
    return;
  }
  if (single.value) {
    clearSingle();
    return;
  }
  close();
}

function onPanelCommit(time: string): void {
  if (props.range) {
    commitRangeSide(time);
    return;
  }
  commitSingle(time);
}

// --- Field ---

const displayText = computed(() => {
  if (!props.range) {
    return text.value;
  }
  const [start, end] = rangeValue.value;
  if (start && end) {
    return t("date_picker.range_text", { from: start, to: end });
  }
  return start ?? "";
});

const shownError = computed(() => typedError.value ?? props.errorMessage ?? null);
const hasError = computed(() => Boolean(shownError.value));
const errorId = computed(() => `${id.value}-error`);
const computedAriaDescribedby = computed(() => {
  const describedByError = hasError.value && props.showErrorBelow ? errorId.value : undefined;
  const parts = [props.ariaDescribedby, describedByError].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
});

const inputPlaceholder = computed(() => {
  if (!props.showErrorBelow && props.errorMessage) {
    return props.errorMessage;
  }
  if (props.placeholder) {
    return props.placeholder;
  }
  if (props.range) {
    return t("date_picker.time_range_placeholder");
  }
  return props.required ? t("date_picker.time_placeholder_required") : t("date_picker.time_placeholder");
});

const dialogLabel = computed(() => (props.range ? t("date_picker.time_range_placeholder") : t("date_picker.time_placeholder_required")));
const isReadonly = computed(() => props.range || isPhone.value || isDisabled.value);
const hasValue = computed(() => {
  if (props.range) {
    return Boolean(rangeValue.value[0] || rangeValue.value[1]);
  }
  return single.value !== null;
});

const frameClasses = computed(() => fieldFrameCva({
  size: props.size,
  state: isDisabled.value ? "disabled" : hasError.value ? "error" : isOpen.value ? "open" : "idle",
}));
const inputClasses = computed(() => fieldInputCva({ disabled: isDisabled.value }));
const clearClasses = computed(() => fieldClearCva());

defineExpose({ open, close });
</script>

<template>
  <div ref="wrapperRef" class="relative w-full">
    <VFieldLabel
      v-if="labelLeft"
      :label="labelLeft"
      :for="id"
      :required="required"
      display="inline"
    />
    <ACTooltip type="edit_field" :allow-change="hasPerms">
      <VPickerPopover
        ref="popover"
        :label="dialogLabel"
        :disabled="isDisabled"
        @show="onShow"
        @hide="onHide"
      >
        <div :class="frameClasses" data-test="time-field" @click="onFieldClick">
          <VIcon
            name="clock"
            class="pointer-events-none absolute left-3.5 text-[15px] text-gray-600 dark:text-gray-400"
            aria-hidden="true"
          />
          <input
            :id="id"
            ref="inputRef"
            :value="displayText"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            :readonly="isReadonly"
            :disabled="disabled"
            :placeholder="inputPlaceholder"
            :aria-required="required || undefined"
            :aria-invalid="hasError ? 'true' : undefined"
            :aria-describedby="computedAriaDescribedby"
            :aria-labelledby="ariaLabelledby"
            aria-haspopup="dialog"
            :aria-expanded="isOpen"
            :class="inputClasses"
            data-test="time-input"
            @input="onFieldInput"
            @keydown="onFieldKeydown"
            @blur="onFieldBlur"
          >
          <button
            v-if="hasValue && !isDisabled"
            type="button"
            :class="clearClasses"
            :aria-label="range ? t('date_picker.clear') : t('date_picker.clear_time')"
            data-test="time-clear-field"
            @click.stop="range ? clearRange() : clearSingle()"
          >
            <VIcon name="x" class="text-sm" aria-hidden="true" />
          </button>
          <span v-else-if="!hasPerms" class="mr-3 inline-flex text-gray-500" aria-hidden="true">
            <VIcon name="lock" class="text-[13px]" />
          </span>
        </div>
        <template #panel="{ sheet }">
          <VTimePanel
            :key="range ? activeSide : 'single'"
            ref="panel"
            :value="panelValue"
            :minute-step="minuteStep"
            :quick-times="quickTimes"
            :show-now="showNow"
            :min-time="panelMinTime"
            :max-time="maxTime"
            :clear-label="clearLabel"
            :sheet="sheet"
            :show-heading="!range"
            @commit="onPanelCommit"
            @clear="onPanelClear"
            @cancel="close"
          >
            <template v-if="range" #heading>
              <div class="inline-flex gap-0.5 self-start rounded-full bg-gray-100 p-[3px] dark:bg-dark-700" role="group" :aria-label="t('date_picker.which_time')">
                <button
                  v-for="side in (['start', 'end'] as const)"
                  :key="side"
                  type="button"
                  class="h-7 rounded-full px-3 text-xs leading-4 font-medium tabular-nums"
                  :class="activeSide === side
                    ? 'bg-white text-primary-900 shadow-[0_1px_2px_rgba(0,0,0,.1)] dark:bg-dark-800 dark:text-primary-300'
                    : 'bg-transparent text-gray-700 dark:text-gray-300'"
                  :aria-pressed="activeSide === side"
                  :data-test="`range-side-${side}`"
                  @click="activeSide = side"
                >
                  {{ side === 'start' ? startLabel : endLabel }}<template v-if="rangeDraft[side === 'start' ? 0 : 1]">
                    {{ rangeDraft[side === 'start' ? 0 : 1] }}
                  </template>
                </button>
              </div>
            </template>
            <template v-if="range && endAdjusted" #notice>
              <div class="flex items-start gap-2 rounded-[10px] bg-warning-100 px-2.5 py-2 text-xs leading-4 text-warning-800" role="status">
                <VIcon name="triangle-alert" class="mt-px" aria-hidden="true" />
                <span>{{ t("date_picker.end_time_adjusted") }}</span>
              </div>
            </template>
          </VTimePanel>
        </template>
      </VPickerPopover>
    </ACTooltip>
    <div v-if="shownError && (showErrorBelow || typedError)" :id="errorId" class="mt-1 text-xs leading-4 text-danger-800 dark:text-danger-400">
      {{ shownError }}
    </div>
  </div>
</template>
